import type { Aabb } from '../sim.js';
import type { Vec3 } from '../math.js';
import type { Heightfield } from '../terrain.js';

/**
 * A **region sidecar**: what a `tools/assetgen/blender/*_region.py` script writes next to the
 * region GLB it exports. The client renders the GLB; the SIM - on both the server and the
 * predicting client - walks the heightfield in here, sampled by the same script from the same
 * sculpted terrain, so what a player sees is exactly what they collide with. `boxes` are the AABB
 * colliders of the set dressing the script placed; the sim only ever collides with boxes + the
 * heightfield, and this keeps that contract.
 *
 * The file is optional. When it is absent (a fresh checkout that hasn't run Blender, a test
 * runner, a deploy that forgot to copy it), the map keeps the in-code procedural terrain and
 * scatter from its own `maps/*.ts`, and the client draws the heightfield mesh. What is not
 * optional is that BOTH ends agree: a server without the sidecar and a client with it (or vice
 * versa) would predict against different ground and rubber-band, so `parseRegion` is strict and
 * every loader goes through it.
 *
 * **Per-map, since the pivot.** There used to be exactly one of these (the Spine's), so the grid
 * was a module constant and the parser took one argument. Lanternfall and the Vigil Downs each
 * have their own now, on their own lattices, so the grid is a parameter (`REGION_SPECS`) and the
 * sidecar carries the `mapId` it was authored for - a Downs sidecar dropped into Lanternfall's
 * slot is refused by name before it is ever refused by geometry.
 */

/** The lattice a sidecar's heightfield must be sampled on. Fixed per map by its `maps/*.ts`. */
export interface RegionGrid {
  originX: number;
  originZ: number;
  cellSize: number;
  cols: number;
  rows: number;
}

/** Where an NPC stands: `pos` is the feet, `yaw` the facing (0 = -Z, like the players). */
export interface RegionStand {
  id: string;
  pos: Vec3;
  yaw: number;
}

/** A sit point on a bench the GLB modelled. `yaw` faces the way a sitter looks. */
export interface RegionBench {
  id: string;
  pos: Vec3;
  yaw: number;
}

/**
 * A lantern the GLB modelled, for `render/life.ts`'s emissive flicker. `pos` is the glass itself
 * (not the post's foot); `warm` picks the colour family. Never a real light - the bible allows
 * one sun and one hemisphere, so a lantern is an emissive material and nothing more.
 */
export interface RegionLantern {
  pos: Vec3;
  warm: boolean;
}

/** A named point the script placed, for docs, the M-key plate and tooling only - never gameplay. */
export interface RegionLandmark {
  name: string;
  x: number;
  z: number;
}

export interface RegionSidecar {
  /** Sidecar format version (`version` in the JSON). */
  version: number;
  /** Which map this was authored for; must match the slot it is loaded into. */
  mapId: string;
  heightfield: Heightfield;
  boxes: Aabb[];
  landmarks: RegionLandmark[];
  npcStands: RegionStand[];
  /**
   * Boxes the map's *own* `maps/*.ts` authored (not this sidecar) whose visuals the region GLB
   * has dressed, matched later by coordinates. Any in-code box the export has not seen simply
   * falls out of the list and keeps its greybox. Empty when the GLB dresses nothing in-code.
   */
  dressedBoxes: Aabb[];
  /** Sit points, for the crowd's sit clip and for `InteractAction` later. */
  benches: RegionBench[];
  /** Emissive lantern glass positions, for the flicker in `render/life.ts`. */
  lanterns: RegionLantern[];
  /** Release points the script modelled (headstones, a wayshrine plinth). */
  graveyards: Vec3[];
  /**
   * Per-cell grass density on exactly the heightfield's lattice (`cols * rows` values, 0..1), or
   * empty for "use the map's own density function". Lets the Blender pass keep grass off a paved
   * plaza, a road and a barrow without the client re-deriving where those are.
   */
  grassMask: number[];
}

/** Back-compat alias: the Spine's sidecar was the only one, and `SpineRegion` is still imported. */
export type SpineRegion = RegionSidecar;

/** The format string of a modern, per-map sidecar. */
export const REGION_FORMAT = 'stillsun-region';
export const REGION_VERSION = 2;

/** The Spine's original single-map format, still on disk and still loadable unchanged. */
export const SPINE_REGION_FORMAT = 'stillsun-spine-region';
export const SPINE_REGION_VERSION = 1;

/** The Spine's grid, fixed by spine.ts: its sidecar has to be sampled on exactly this lattice. */
export const SPINE_GRID: RegionGrid = {
  originX: -132,
  originZ: -84,
  cellSize: 4,
  cols: 67,
  rows: 43,
};

/**
 * Lanternfall's grid: 4 m cells over -64..64, one cell of margin outside the 120 m walls.
 *
 * The city is **flat by design** - every height on this lattice is 0 in the in-code map, and the
 * Blender pass is only allowed to author steps as colliders, never as terrain. That is not
 * fussiness: every `y: 0` in `lanternfall.ts` (spawns, npcStands, waypoints, the graveyard) would
 * otherwise become a terrain query, and the 300-bot perf gate stands in this plaza. A flat
 * heightfield is exactly the ground those numbers already assume, so adding one changes nothing
 * the sim can measure while giving the client the terrain branch it needs to attach a region GLB.
 */
export const LANTERNFALL_GRID: RegionGrid = {
  originX: -64,
  originZ: -64,
  cellSize: 4,
  cols: 33,
  rows: 33,
};

/** The Vigil Downs' grid, fixed by vigil-downs.ts: 8 m cells over 512 m from (-256, -256). */
export const DOWNS_GRID: RegionGrid = {
  originX: -256,
  originZ: -256,
  cellSize: 8,
  cols: 65,
  rows: 65,
};

/**
 * The most one cell may rise. `stepPlayer`'s grounded terrain-follow only snaps the feet up
 * `STEP_DOWN` (0.6 m) per tick; a sprinting player covers ~0.29 m of ground per tick, so a face
 * steeper than ~2:1 would leave them walking inside the terrain. The limit is therefore a
 * *slope*, 1.5:1, expressed per cell - which is 6 m on the Spine's 4 m grid and 12 m on the
 * Downs' 8 m one, the same walkability either way.
 */
export const REGION_MAX_SLOPE = 1.5;

/** Back-compat: the Spine's 4 m grid at the 1.5:1 slope limit. */
export const SPINE_REGION_MAX_CELL_RISE = SPINE_GRID.cellSize * REGION_MAX_SLOPE;

/** Everything `parseRegion` needs to judge one map's sidecar. */
export interface RegionSpec {
  mapId: string;
  grid: RegionGrid;
  /** Accepted `format` string. */
  format: string;
  /** Accepted `version`. */
  version: number;
  /**
   * Legacy sidecars predate `mapId` and the extra point lists; their absence is not an error.
   * Only the Spine's v1 file sets this.
   */
  legacy?: boolean;
}

/** The one place a map id is turned into the rules its sidecar is judged by. */
export const REGION_SPECS: Readonly<Record<string, RegionSpec>> = {
  spine: {
    mapId: 'spine',
    grid: SPINE_GRID,
    format: SPINE_REGION_FORMAT,
    version: SPINE_REGION_VERSION,
    legacy: true,
  },
  lanternfall: {
    mapId: 'lanternfall',
    grid: LANTERNFALL_GRID,
    format: REGION_FORMAT,
    version: REGION_VERSION,
  },
  'vigil-downs': {
    mapId: 'vigil-downs',
    grid: DOWNS_GRID,
    format: REGION_FORMAT,
    version: REGION_VERSION,
  },
};

/** Where a map's sidecar and GLB live under the client's public assets. */
export function regionAssetBase(mapId: string): string {
  return `/assets/gen/region/${mapId === 'spine' ? 'spine_region' : `${mapId}_region`}`;
}

function num(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v))
    throw new Error(`region: ${what} is not a finite number`);
  return v;
}

function vec3(v: unknown, what: string): Vec3 {
  if (typeof v !== 'object' || v === null) throw new Error(`region: ${what} is not an object`);
  const o = v as Record<string, unknown>;
  return { x: num(o['x'], `${what}.x`), y: num(o['y'], `${what}.y`), z: num(o['z'], `${what}.z`) };
}

function aabb(v: unknown, what: string): Aabb {
  if (typeof v !== 'object' || v === null) throw new Error(`region: ${what} is not an object`);
  const o = v as Record<string, unknown>;
  const min = vec3(o['min'], `${what}.min`);
  const max = vec3(o['max'], `${what}.max`);
  if (!(min.x < max.x && min.y < max.y && min.z < max.z))
    throw new Error(`region: ${what} is inside out`);
  return { min, max };
}

function list(v: unknown, what: string): unknown[] {
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v)) throw new Error(`region: ${what} must be an array`);
  return v;
}

/**
 * Validates a parsed region sidecar against one map's spec and returns the typed region, or
 * throws with a reason. Strict on purpose: a sidecar on the wrong grid, one authored for another
 * map, or one steeper than the sim can walk must be refused loudly rather than half-applied.
 */
export function parseRegion(json: unknown, spec: RegionSpec): RegionSidecar {
  if (typeof json !== 'object' || json === null) throw new Error('region: not an object');
  const root = json as Record<string, unknown>;
  if (root['format'] !== spec.format)
    throw new Error(`region: unexpected format ${String(root['format'])}`);
  const version = num(root['version'], 'version');
  if (version !== spec.version)
    throw new Error(`region: version ${version}, expected ${spec.version}`);
  const mapId = spec.legacy ? spec.mapId : String(root['mapId'] ?? '');
  if (mapId !== spec.mapId)
    throw new Error(`region: mapId ${JSON.stringify(mapId)}, expected ${spec.mapId}`);

  const hf = root['heightfield'];
  if (typeof hf !== 'object' || hf === null) throw new Error('region: missing heightfield');
  const h = hf as Record<string, unknown>;
  const grid: RegionGrid = {
    originX: num(h['originX'], 'heightfield.originX'),
    originZ: num(h['originZ'], 'heightfield.originZ'),
    cellSize: num(h['cellSize'], 'heightfield.cellSize'),
    cols: num(h['cols'], 'heightfield.cols'),
    rows: num(h['rows'], 'heightfield.rows'),
  };
  for (const key of Object.keys(spec.grid) as (keyof RegionGrid)[]) {
    if (grid[key] !== spec.grid[key])
      throw new Error(`region: heightfield.${key} is ${grid[key]}, expected ${spec.grid[key]}`);
  }
  const rawHeights = root['heightfield'] as Record<string, unknown>;
  const heightsIn = rawHeights['heights'];
  if (!Array.isArray(heightsIn) || heightsIn.length !== grid.cols * grid.rows) {
    throw new Error(`region: heights must have ${grid.cols * grid.rows} entries`);
  }
  const heights: number[] = heightsIn.map((v, i) => num(v, `heights[${i}]`));
  const maxRise = grid.cellSize * REGION_MAX_SLOPE;
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const h0 = heights[r * grid.cols + c]!;
      const east = c + 1 < grid.cols ? heights[r * grid.cols + c + 1]! : h0;
      const south = r + 1 < grid.rows ? heights[(r + 1) * grid.cols + c]! : h0;
      const rise = Math.max(Math.abs(east - h0), Math.abs(south - h0));
      if (rise > maxRise) {
        throw new Error(
          `region: cell (${c},${r}) rises ${rise.toFixed(2)} m, over the ${maxRise} m walkable limit`,
        );
      }
    }
  }

  const boxes: Aabb[] = list(root['boxes'], 'boxes').map((b, i) => aabb(b, `boxes[${i}]`));

  const landmarks: RegionLandmark[] = list(root['landmarks'], 'landmarks').map((l, i) => {
    if (typeof l !== 'object' || l === null)
      throw new Error(`region: landmarks[${i}] is not an object`);
    const o = l as Record<string, unknown>;
    return {
      name: String(o['name'] ?? ''),
      x: num(o['x'], `landmarks[${i}].x`),
      z: num(o['z'], `landmarks[${i}].z`),
    };
  });

  const npcStands: RegionStand[] = list(root['npcStands'], 'npcStands').map((s, i) => {
    if (typeof s !== 'object' || s === null)
      throw new Error(`region: npcStands[${i}] is not an object`);
    const o = s as Record<string, unknown>;
    return {
      id: String(o['id'] ?? ''),
      pos: vec3(o['pos'], `npcStands[${i}].pos`),
      yaw: num(o['yaw'], `npcStands[${i}].yaw`),
    };
  });

  // `wreckKit` is v1's name for the same idea; both are read so the Spine's file still loads.
  const dressedRaw = root['dressedBoxes'] ?? root['wreckKit'];
  const dressedBoxes: Aabb[] = list(dressedRaw, 'dressedBoxes').map((b, i) =>
    aabb(b, `dressedBoxes[${i}]`),
  );

  const benches: RegionBench[] = list(root['benches'], 'benches').map((b, i) => {
    if (typeof b !== 'object' || b === null)
      throw new Error(`region: benches[${i}] is not an object`);
    const o = b as Record<string, unknown>;
    return {
      id: String(o['id'] ?? `bench-${i}`),
      pos: vec3(o['pos'], `benches[${i}].pos`),
      yaw: num(o['yaw'], `benches[${i}].yaw`),
    };
  });

  const lanterns: RegionLantern[] = list(root['lanterns'], 'lanterns').map((l, i) => {
    if (typeof l !== 'object' || l === null)
      throw new Error(`region: lanterns[${i}] is not an object`);
    const o = l as Record<string, unknown>;
    return { pos: vec3(o['pos'], `lanterns[${i}].pos`), warm: o['warm'] !== false };
  });

  const graveyards: Vec3[] = list(root['graveyards'], 'graveyards').map((g, i) =>
    vec3(g, `graveyards[${i}]`),
  );

  const grassRaw = list(root['grassMask'], 'grassMask');
  if (grassRaw.length !== 0 && grassRaw.length !== grid.cols * grid.rows) {
    throw new Error(`region: grassMask must be empty or ${grid.cols * grid.rows} entries`);
  }
  const grassMask: number[] = grassRaw.map((v, i) =>
    Math.min(1, Math.max(0, num(v, `grassMask[${i}]`))),
  );

  return {
    version,
    mapId,
    heightfield: { ...grid, heights },
    boxes,
    landmarks,
    npcStands,
    dressedBoxes,
    benches,
    lanterns,
    graveyards,
    grassMask,
  };
}

/** Back-compat wrapper: the Spine's v1 sidecar, on the Spine's grid. */
export function parseSpineRegion(json: unknown): RegionSidecar {
  return parseRegion(json, REGION_SPECS['spine']!);
}
